import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";

import { Baseurl } from "../../config/BaseUrl";

// 1️⃣ Fetch Subcategories by Category ID
export const fetchSubCategories = createAsyncThunk(
  "products/fetchSubCategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/subcategory/subcategoryid/${categoryId}`
      );
      if (res.data.success) return res.data.subcategory;
      return rejectWithValue("Failed to load subcategories");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// 2️⃣ Fetch Tags by Subcategory ID
export const fetchTags = createAsyncThunk(
  "products/fetchTags",
  async (subcatId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/categorytag/tagsbysubcat/${subcatId}`
      );
      if (res.data.success) return res.data.tags;
      return rejectWithValue("Failed to load tags");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// 3️⃣ Fetch Products by Tag ID
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async ({ tagId, cartItems }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/product/productbyCatTagid/${tagId}`
      );
      if (res.data.success) {
        const filtered = res.data.product.filter((p) => !p.outOfStock);

        // Merge cart quantities
        const withQty = filtered.map((product) => {
          const cartItem = cartItems.find(
            (item) => item.ProductId === product._id
          );
          return {
            ...product,
            cartQuantity: cartItem ? cartItem.Qty : 0,
          };
        });

        return withQty;
      }
      return rejectWithValue("Failed to load products");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const productFlowSlice = createSlice({
  name: "productFlow",
  initialState: {
    singleCat: null,
    subcategories: [],
    tags: [],
    products: [],
    selectedSubcat: null,
    selectedTag: null,
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedSubcat: (state, action) => {
      state.selectedSubcat = action.payload;
      state.tags = [];
      state.selectedTag = null;
      state.products = [];
    },
    setSelectedTag: (state, action) => {
      state.selectedTag = action.payload;
      state.products = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // 󠀠Subcategories
      .addCase(fetchSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.subcategories = action.payload;
        state.selectedSubcat = action.payload[0] || null;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // Tags
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.loading = false;
        state.tags = action.payload;
        state.selectedTag = action.payload[0] || null;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload);
      });
  },
});

export const { setSelectedSubcat, setSelectedTag } = productFlowSlice.actions;
export default productFlowSlice.reducer;
